/**
 * Custom hook to handle UTM parameters and invite code tracking
 * Manages:
 * - UTM parameter persistence
 * - Invite code tracking
 * - Local storage management
 * - URL parameter handling
 */

// React core imports
import { useState, useEffect } from 'react'
import { useSearchParams } from 'next/navigation'

// External libraries and utilities
import localStorage from '@locmod/local-storage'

// Internal application modules
import { constants } from 'helpers'

/**
 * Interface defining the structure of UTM parameters
 * Used for tracking marketing campaign data
 */
interface UtmParams {
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_term: string
  utm_content: string
}

const { localStorageKeys } = constants

export const useUtmTracking = () => {
  // Get URL search parameters
  const params = useSearchParams()
  const [hasStored, setHasStored] = useState(false)

  useEffect(() => {
    // Skip if running on server or already stored
    if (typeof window === 'undefined' || hasStored) {
      return
    }

    // Get invite code from URL
    const inviteCode = params.get(localStorageKeys.inviteCode)

    // Build UTM parameters object, with URL params
    const utmParams: UtmParams = {
      utm_source: params.get(localStorageKeys.utmSource) || '',
      utm_medium: params.get(localStorageKeys.utmMedium) || '',
      utm_campaign: params.get(localStorageKeys.utmCampaign) || '',
      utm_term: params.get(localStorageKeys.utmTerm) || '',
      utm_content: params.get(localStorageKeys.utmContent) || '',
    }

    localStorage.setItem(localStorageKeys.inviteCode, inviteCode)
    // Store values if UTM params are present
    if (Object.values(utmParams).some(value => value !== '')) {
      localStorage.setItem(
        localStorageKeys.utmParams,
        JSON.stringify(utmParams)
      )
    }
    setHasStored(true)
  }, [params, hasStored])

  // Return current stored values
  return {
    storedInviteCode: localStorage.getItem(localStorageKeys.inviteCode),
    storedUtmParams: JSON.parse(
      localStorage.getItem(localStorageKeys.utmParams) || '{}'
    ) as UtmParams,
  }
}
